export interface CXTranslations {
  // Header
  roleplayPractice: string;
  serviceRecovery: string;
  endSession: string;

  // Loading states
  preparingVoiceSession: string;
  connectingToCustomer: string;
  analyzingPerformance: string;
  generatingFeedback: string;
  preparingChatSession: string;
  settingUpConversation: string;

  // Practice controls
  startVoicePractice: string;
  startChatPractice: string;
  voicePractice: string;
  chatPractice: string;
  choosePracticeMode: string;
  realCallSimulation: string;
  textBasedPractice: string;

  // Customer states
  customerIsSpeaking: string;
  customerIsListening: string;

  // Section titles
  practiceDetails: string;
  practiceObjectives: string;
  mainIssue: string;
  serviceDelivery: string;
  customerDetails: string;
  policyInformation: string;

  // Service delivery subsections
  greetingIdentityVerification: string;
  problemUnderstanding: string;
  empathyTone: string;
  resolutionRecovery: string;

  // Customer details fields
  orderId: string;
  items: string;
  promisedVsActual: string;
  location: string;
  accountInformation: string;
  recentIncidents: string;
  signedUpOn: string;
  subscription: string;
  loyaltyStatus: string;
  bookingReference: string;

  // Service details fields (for Miso)
  serviceType: string;
  scheduledVsActual: string;
  issue: string;

  // Policy fields
  trigger: string;
  remedy: string;
  policies: string;

  // Policy titles (for Cathay scenarios)
  typhoonDisruption: string;
  businessClassPriority: string;
  safetyRegulations: string;
  culturalSensitivity: string;
  shortHaulAmenities: string;
  businessClassService: string;
  customerNeedsDiscovery: string;
  passengerCelebration: string;
  teamCollaboration: string;
  resourcefulness: string;

  // Chat interface
  typeYourResponse: string;

  // Error states
  conversationTooBrief: string;
  conversationTooBriefMessage: string;
  tryAgain: string;

  // Assessment page
  roleplayAssessment: string;
  overallScore: string;
  excellent: string;
  good: string;
  fair: string;
  poor: string;
  serviceDeliveryScore: string;
  policyApplication: string;
  practiceInfo: string;
  type: string;
  guest: string;
  completedAt: string;
  practiceAgain: string;
  jumpToSection: string;
  overview: string;
  summary: string;
  suggestedNextSteps: string;
  why: string;
  suggestion: string;
  strengths: string;
  toImprove: string;
  correction: string;
  overallScoreLabel: string;

  // Assessment descriptions
  serviceDeliveryDesc: string;
  policyApplicationDesc: string;

  // Loading and error states
  loadingAssessmentData: string;
  failedToLoadAssessment: string;
  failedToLoadAssessmentData: string;
  returnToPractice: string;
  noAssessmentAvailable: string;
  noAssessmentDataAvailable: string;
}

export const translations: Record<string, CXTranslations> = {
  en: {
    // Header
    roleplayPractice: 'Roleplay practice',
    serviceRecovery: 'Service recovery',
    endSession: 'End session',

    // Loading states
    preparingVoiceSession: 'Preparing Voice Session',
    connectingToCustomer: 'Connecting to {name} and setting up audio...',
    analyzingPerformance: 'Analyzing Your Performance',
    generatingFeedback: 'Generating detailed feedback and insights...',
    preparingChatSession: 'Preparing Chat Session',
    settingUpConversation: 'Setting up your conversation with {name}...',

    // Practice controls
    startVoicePractice: 'Start Voice Practice',
    startChatPractice: 'Start Chat Practice',
    voicePractice: 'Voice',
    chatPractice: 'Chat',
    choosePracticeMode: 'Select mode',
    realCallSimulation: 'Call practice',
    textBasedPractice: 'Text practice',

    // Customer states
    customerIsSpeaking: 'Customer is speaking...',
    customerIsListening: 'Customer is listening...',

    // Section titles
    practiceDetails: 'Practice details',
    practiceObjectives: 'Practice objectives',
    mainIssue: 'Main issue',
    serviceDelivery: 'Service delivery',
    customerDetails: 'Customer details',
    policyInformation: 'Policy information',

    // Service delivery subsections
    greetingIdentityVerification: 'Greeting & Identity Verification',
    problemUnderstanding: 'Problem Understanding',
    empathyTone: 'Empathy & Tone',
    resolutionRecovery: 'Resolution & Recovery',

    // Customer details fields
    orderId: 'Order ID',
    items: 'Items',
    promisedVsActual: 'Promised ETA vs. Actual drop off',
    location: 'Location',
    accountInformation: 'Account information',
    recentIncidents: 'Incidents reported (last 30 days)',
    signedUpOn: 'Signed up on',
    subscription: 'Subscription',
    loyaltyStatus: 'Loyalty Status',
    bookingReference: 'Booking Reference',

    // Service details fields
    serviceType: 'Service Type',
    scheduledVsActual: 'Scheduled vs. Actual',
    issue: 'Issue',

    // Policy fields
    trigger: 'Trigger',
    remedy: 'Remedy',
    policies: 'policies',

    // Policy titles (for Cathay scenarios)
    typhoonDisruption: 'Typhoon Disruption',
    businessClassPriority: 'Business Class Priority',
    safetyRegulations: 'Safety Regulations',
    culturalSensitivity: 'Cultural Sensitivity',
    shortHaulAmenities: 'Short Haul Amenities',
    businessClassService: 'Business Class Service',
    customerNeedsDiscovery: 'Customer Needs Discovery',
    passengerCelebration: 'Passenger Celebration',
    teamCollaboration: 'Team Collaboration',
    resourcefulness: 'Resourcefulness',

    // Chat interface
    typeYourResponse: 'Type your response...',

    // Error states
    conversationTooBrief: 'Conversation Too Brief',
    conversationTooBriefMessage: 'Your conversation was too brief to generate a meaningful assessment. Please try again with a longer conversation that includes more detailed responses.',
    tryAgain: 'Try Again',

    // Assessment page
    roleplayAssessment: 'Roleplay session assessment',
    overallScore: 'Overall score',
    excellent: 'Excellent',
    good: 'Good',
    fair: 'Fair',
    poor: 'Poor',
    serviceDeliveryScore: 'Service Delivery',
    policyApplication: 'Policy Application',
    practiceInfo: 'Practice info',
    type: 'Type',
    guest: 'Guest',
    completedAt: 'Completed at',
    practiceAgain: 'Practice again',
    jumpToSection: 'Jump to section',
    overview: 'Overview',
    summary: 'Summary',
    suggestedNextSteps: 'Suggested next steps',
    why: 'Why',
    suggestion: 'Suggestion',
    strengths: 'Strengths',
    toImprove: 'To improve',
    correction: 'Correction',
    overallScoreLabel: 'Overall score',

    // Assessment descriptions
    serviceDeliveryDesc: 'Assesses how effectively the interaction was handled from a communication and customer experience standpoint.',
    policyApplicationDesc: 'Assesses how accurately company policies are applied and how well internal standards and protocols are followed.',

    // Loading and error states
    loadingAssessmentData: 'Loading assessment data...',
    failedToLoadAssessment: 'Failed to load assessment',
    failedToLoadAssessmentData: 'Failed to load assessment data',
    returnToPractice: 'Return to practice',
    noAssessmentAvailable: 'No assessment available',
    noAssessmentDataAvailable: 'There is no assessment data available for this session.'
  },

  ko: {
    // Header
    roleplayPractice: '롤플레이 연습',
    serviceRecovery: '서비스 회복',
    endSession: '세션 종료',

    // Loading states
    preparingVoiceSession: '음성 세션 준비 중',
    connectingToCustomer: '{name}님과 연결하고 음성을 설정하는 중...',
    analyzingPerformance: '성과 분석 중',
    generatingFeedback: '상세한 피드백과 인사이트를 생성하는 중...',
    preparingChatSession: '채팅 세션 준비 중',
    settingUpConversation: '{name}님과의 대화를 설정하는 중...',

    // Practice controls
    startVoicePractice: '음성 연습 시작',
    startChatPractice: '채팅 연습 시작',
    voicePractice: '음성',
    chatPractice: '채팅',
    choosePracticeMode: '방식 선택',
    realCallSimulation: '통화 연습',
    textBasedPractice: '텍스트 연습',

    // Customer states
    customerIsSpeaking: '고객이 말하는 중...',
    customerIsListening: '고객이 듣는 중...',

    // Section titles
    practiceDetails: '연습 세부사항',
    practiceObjectives: '연습 목표',
    mainIssue: '주요 문제',
    serviceDelivery: '서비스 전달',
    customerDetails: '고객 세부정보',
    policyInformation: '정책 정보',

    // Service delivery subsections
    greetingIdentityVerification: '인사 및 신원 확인',
    problemUnderstanding: '문제 파악',
    empathyTone: '공감과 어조',
    resolutionRecovery: '해결 및 회복',

    // Customer details fields
    orderId: '주문 ID',
    items: '항목',
    promisedVsActual: '약속 시간 vs. 실제 도착',
    location: '위치',
    accountInformation: '계정 정보',
    recentIncidents: '최근 신고 내역 (30일)',
    signedUpOn: '가입일',
    subscription: '구독',
    loyaltyStatus: '로열티 상태',
    bookingReference: '예약 번호',

    // Service details fields
    serviceType: '서비스 유형',
    scheduledVsActual: '예약 시간 vs. 실제',
    issue: '문제',

    // Policy fields
    trigger: '트리거',
    remedy: '해결책',
    policies: '정책',

    // Policy titles (for Cathay scenarios)
    typhoonDisruption: '태풍 운항 중단',
    businessClassPriority: '비즈니스 클래스 우선 처리',
    safetyRegulations: '안전 규정',
    culturalSensitivity: '문화적 민감성',
    shortHaulAmenities: '단거리 노선 편의용품',
    businessClassService: '비즈니스 클래스 서비스',
    customerNeedsDiscovery: '고객 요구 파악',
    passengerCelebration: '승객 축하 서비스',
    teamCollaboration: '팀 협업',
    resourcefulness: '문제 해결력',

    // Chat interface
    typeYourResponse: '응답을 입력하세요...',

    // Error states
    conversationTooBrief: '대화가 너무 짧음',
    conversationTooBriefMessage: '의미 있는 평가를 생성하기에는 대화가 너무 짧았습니다. 더 자세한 응답을 포함한 긴 대화로 다시 시도해 주세요.',
    tryAgain: '다시 시도',

    // Assessment page
    roleplayAssessment: '롤플레이 세션 평가',
    overallScore: '전체 점수',
    excellent: '우수',
    good: '양호',
    fair: '보통',
    poor: '미흡',
    serviceDeliveryScore: '서비스 전달',
    policyApplication: '정책 적용',
    practiceInfo: '연습 정보',
    type: '유형',
    guest: '고객',
    completedAt: '완료 시간',
    practiceAgain: '다시 연습하기',
    jumpToSection: '섹션으로 이동',
    overview: '개요',
    summary: '요약',
    suggestedNextSteps: '다음 단계 제안',
    why: '이유',
    suggestion: '제안',
    strengths: '강점',
    toImprove: '개선 필요',
    correction: '수정',
    overallScoreLabel: '전체 점수',

    // Assessment descriptions
    serviceDeliveryDesc: '커뮤니케이션 및 고객 경험 관점에서 상호작용이 얼마나 효과적으로 처리되었는지 평가합니다.',
    policyApplicationDesc: '회사 정책이 얼마나 정확하게 적용되고 내부 표준과 프로토콜이 얼마나 잘 준수되는지 평가합니다.',

    // Loading and error states
    loadingAssessmentData: '평가 데이터 로드 중...',
    failedToLoadAssessment: '평가 로드 실패',
    failedToLoadAssessmentData: '평가 데이터 로드 실패',
    returnToPractice: '연습으로 돌아가기',
    noAssessmentAvailable: '평가 사용 불가',
    noAssessmentDataAvailable: '이 세션에 대한 평가 데이터가 없습니다.'
  },

  id: {
    // Header
    roleplayPractice: 'Latihan bermain peran',
    serviceRecovery: 'Pemulihan layanan',
    endSession: 'Akhiri sesi',

    // Loading states
    preparingVoiceSession: 'Menyiapkan Sesi Suara',
    connectingToCustomer: 'Menghubungkan ke {name} dan mengatur audio...',
    analyzingPerformance: 'Menganalisis Performa Anda',
    generatingFeedback: 'Membuat umpan balik dan wawasan yang detail...',
    preparingChatSession: 'Menyiapkan Sesi Chat',
    settingUpConversation: 'Mengatur percakapan Anda dengan {name}...',

    // Practice controls
    startVoicePractice: 'Mulai Latihan Suara',
    startChatPractice: 'Mulai Latihan Chat',
    voicePractice: 'Suara',
    chatPractice: 'Chat',
    choosePracticeMode: 'Pilih mode',
    realCallSimulation: 'Latihan panggilan',
    textBasedPractice: 'Latihan teks',

    // Customer states
    customerIsSpeaking: 'Pelanggan sedang berbicara...',
    customerIsListening: 'Pelanggan sedang mendengarkan...',

    // Section titles
    practiceDetails: 'DETAIL LATIHAN',
    practiceObjectives: 'Tujuan latihan',
    mainIssue: 'Masalah utama',
    serviceDelivery: 'PENYAMPAIAN LAYANAN',
    customerDetails: 'DETAIL PELANGGAN',
    policyInformation: 'INFORMASI KEBIJAKAN',

    // Service delivery subsections
    greetingIdentityVerification: 'Salam & Verifikasi Identitas',
    problemUnderstanding: 'Pemahaman Masalah',
    empathyTone: 'Empati & Nada',
    resolutionRecovery: 'Resolusi & Pemulihan',

    // Customer details fields
    orderId: 'ID Pesanan',
    items: 'Item',
    promisedVsActual: 'ETA yang Dijanjikan vs. Pengantaran Aktual',
    location: 'Lokasi',
    accountInformation: 'Informasi akun',
    recentIncidents: 'Insiden yang dilaporkan (30 hari terakhir)',
    signedUpOn: 'Mendaftar pada',
    subscription: 'Berlangganan',
    loyaltyStatus: 'Status Loyalitas',
    bookingReference: 'Referensi Pemesanan',

    // Service details fields
    serviceType: 'Jenis Layanan',
    scheduledVsActual: 'Terjadwal vs. Aktual',
    issue: 'Masalah',

    // Policy fields
    trigger: 'Pemicu',
    remedy: 'Solusi',
    policies: 'kebijakan',

    // Policy titles (for Cathay scenarios)
    typhoonDisruption: 'Gangguan Topan',
    businessClassPriority: 'Prioritas Kelas Bisnis',
    safetyRegulations: 'Peraturan Keselamatan',
    culturalSensitivity: 'Sensitivitas Budaya',
    shortHaulAmenities: 'Fasilitas Penerbangan Jarak Pendek',
    businessClassService: 'Layanan Kelas Bisnis',
    customerNeedsDiscovery: 'Identifikasi Kebutuhan Pelanggan',
    passengerCelebration: 'Perayaan Penumpang',
    teamCollaboration: 'Kolaborasi Tim',
    resourcefulness: 'Daya Kreatif',

    // Chat interface
    typeYourResponse: 'Ketik respons Anda...',

    // Error states
    conversationTooBrief: 'Percakapan Terlalu Singkat',
    conversationTooBriefMessage: 'Percakapan Anda terlalu singkat untuk menghasilkan penilaian yang bermakna. Silakan coba lagi dengan percakapan yang lebih panjang yang mencakup respons yang lebih detail.',
    tryAgain: 'Coba Lagi',

    // Assessment page
    roleplayAssessment: 'Penilaian sesi bermain peran',
    overallScore: 'Skor keseluruhan',
    excellent: 'Sangat baik',
    good: 'Baik',
    fair: 'Cukup',
    poor: 'Kurang',
    serviceDeliveryScore: 'Penyampaian Layanan',
    policyApplication: 'Penerapan Kebijakan',
    practiceInfo: 'Info latihan',
    type: 'Tipe',
    guest: 'Tamu',
    completedAt: 'Selesai pada',
    practiceAgain: 'Latihan lagi',
    jumpToSection: 'Lompat ke bagian',
    overview: 'Tinjauan',
    summary: 'Ringkasan',
    suggestedNextSteps: 'Langkah selanjutnya yang disarankan',
    why: 'Mengapa',
    suggestion: 'Saran',
    strengths: 'Kekuatan',
    toImprove: 'Untuk diperbaiki',
    correction: 'Koreksi',
    overallScoreLabel: 'Skor keseluruhan',

    // Assessment descriptions
    serviceDeliveryDesc: 'Menilai seberapa efektif interaksi ditangani dari sudut pandang komunikasi dan pengalaman pelanggan.',
    policyApplicationDesc: 'Menilai seberapa akurat kebijakan perusahaan diterapkan dan seberapa baik standar dan protokol internal diikuti.',

    // Loading and error states
    loadingAssessmentData: 'Memuat data penilaian...',
    failedToLoadAssessment: 'Gagal memuat penilaian',
    failedToLoadAssessmentData: 'Gagal memuat data penilaian',
    returnToPractice: 'Kembali ke latihan',
    noAssessmentAvailable: 'Penilaian tidak tersedia',
    noAssessmentDataAvailable: 'Tidak ada data penilaian yang tersedia untuk sesi ini.'
  },

  th: {
    // Header
    roleplayPractice: 'ฝึกปฏิบัติการสวมบทบาท',
    serviceRecovery: 'การกู้คืนบริการ',
    endSession: 'จบเซสชัน',

    // Loading states
    preparingVoiceSession: 'กำลังเตรียมเซสชันเสียง',
    connectingToCustomer: 'กำลังเชื่อมต่อกับ {name} และตั้งค่าเสียง...',
    analyzingPerformance: 'กำลังวิเคราะห์ประสิทธิภาพของคุณ',
    generatingFeedback: 'กำลังสร้างข้อเสนอแนะและข้อมูลเชิงลึกโดยละเอียด...',
    preparingChatSession: 'กำลังเตรียมเซสชันแชท',
    settingUpConversation: 'กำลังตั้งค่าการสนทนาของคุณกับ {name}...',

    // Practice controls
    startVoicePractice: 'เริ่มฝึกปฏิบัติด้วยเสียง',
    startChatPractice: 'เริ่มฝึกปฏิบัติด้วยแชท',
    voicePractice: 'เสียง',
    chatPractice: 'แชท',
    choosePracticeMode: 'เลือกรูปแบบ',
    realCallSimulation: 'ฝึกปฏิบัติการโทร',
    textBasedPractice: 'ฝึกปฏิบัติด้วยข้อความ',

    // Customer states
    customerIsSpeaking: 'ลูกค้ากำลังพูด...',
    customerIsListening: 'ลูกค้ากำลังฟัง...',

    // Section titles
    practiceDetails: 'รายละเอียดการฝึกปฏิบัติ',
    practiceObjectives: 'วัตถุประสงค์การฝึกปฏิบัติ',
    mainIssue: 'ปัญหาหลัก',
    serviceDelivery: 'การส่งมอบบริการ',
    customerDetails: 'รายละเอียดลูกค้า',
    policyInformation: 'ข้อมูลนโยบาย',

    // Service delivery subsections
    greetingIdentityVerification: 'การทักทายและการยืนยันตัวตน',
    problemUnderstanding: 'การทำความเข้าใจปัญหา',
    empathyTone: 'การเอาใจใส่และน้ำเสียง',
    resolutionRecovery: 'การแก้ไขและการกู้คืน',

    // Customer details fields
    orderId: 'รหัสคำสั่งซื้อ',
    items: 'รายการสินค้า',
    promisedVsActual: 'เวลาที่สัญญา เทียบกับ เวลาส่งของจริง',
    location: 'สถานที่',
    accountInformation: 'ข้อมูลบัญชี',
    recentIncidents: 'เหตุการณ์ที่รายงาน (30 วันที่ผ่านมา)',
    signedUpOn: 'สมัครสมาชิกเมื่อ',
    subscription: 'การสมัครสมาชิก',
    loyaltyStatus: 'สถานะความภักดี',
    bookingReference: 'รหัสการจอง',

    // Service details fields
    serviceType: 'ประเภทบริการ',
    scheduledVsActual: 'ที่กำหนดไว้ เทียบกับ ที่เกิดขึ้นจริง',
    issue: 'ปัญหา',

    // Policy fields
    trigger: 'ปัจจัยกระตุ้น',
    remedy: 'วิธีแก้ไข',
    policies: 'นโยบาย',

    // Policy titles (for Cathay scenarios)
    typhoonDisruption: 'การหยุดชะงักจากพายุไต้ฝุ่น',
    businessClassPriority: 'ลำดับความสำคัญของชั้นธุรกิจ',
    safetyRegulations: 'ข้อบังคับด้านความปลอดภัย',
    culturalSensitivity: 'ความไวต่อวัฒนธรรม',
    shortHaulAmenities: 'สิ่งอำนวยความสะดวกเที่ยวบินระยะสั้น',
    businessClassService: 'บริการชั้นธุรกิจ',
    customerNeedsDiscovery: 'การค้นหาความต้องการลูกค้า',
    passengerCelebration: 'การฉลองของผู้โดยสาร',
    teamCollaboration: 'การทำงานเป็นทีม',
    resourcefulness: 'ความสามารถในการแก้ปัญหา',

    // Chat interface
    typeYourResponse: 'พิมพ์การตอบสนองของคุณ...',

    // Error states
    conversationTooBrief: 'การสนทนาสั้นเกินไป',
    conversationTooBriefMessage: 'การสนทนาของคุณสั้นเกินไปที่จะสร้างการประเมินที่มีความหมาย กรุณาลองใหม่อีกครั้งด้วยการสนทนาที่ยาวกว่าซึ่งรวมถึงการตอบสนองที่มีรายละเอียดมากขึ้น',
    tryAgain: 'ลองใหม่อีกครั้ง',

    // Assessment page
    roleplayAssessment: 'การประเมินเซสชันการสวมบทบาท',
    overallScore: 'คะแนนรวม',
    excellent: 'ยอดเยี่ยม',
    good: 'ดี',
    fair: 'พอใช้',
    poor: 'ต้องปรับปรุง',
    serviceDeliveryScore: 'การส่งมอบบริการ',
    policyApplication: 'การประยุกต์ใช้นโยบาย',
    practiceInfo: 'ข้อมูลการฝึกปฏิบัติ',
    type: 'ประเภท',
    guest: 'แขก',
    completedAt: 'เสร็จสิ้นเมื่อ',
    practiceAgain: 'ฝึกปฏิบัติอีกครั้ง',
    jumpToSection: 'ไปที่ส่วน',
    overview: 'ภาพรวม',
    summary: 'สรุป',
    suggestedNextSteps: 'ขั้นตอนถัดไปที่แนะนำ',
    why: 'เหตุผล',
    suggestion: 'คำแนะนำ',
    strengths: 'จุดแข็ง',
    toImprove: 'ที่ควรปรับปรุง',
    correction: 'การแก้ไข',
    overallScoreLabel: 'คะแนนรวม',

    // Assessment descriptions
    serviceDeliveryDesc: 'ประเมินว่าการโต้ตอบได้รับการจัดการอย่างมีประสิทธิภาพเพียงใดจากมุมมองการสื่อสารและประสบการณ์ลูกค้า',
    policyApplicationDesc: 'ประเมินว่านโยบายของบริษัทถูกนำไปใช้อย่างถูกต้องเพียงใดและมาตรฐานและระเบียบภายในได้รับการปฏิบัติตามได้ดีเพียงใด',

    // Loading and error states
    loadingAssessmentData: 'กำลังโหลดข้อมูลการประเมิน...',
    failedToLoadAssessment: 'ไม่สามารถโหลดการประเมินได้',
    failedToLoadAssessmentData: 'ไม่สามารถโหลดข้อมูลการประเมินได้',
    returnToPractice: 'กลับไปยังการฝึกปฏิบัติ',
    noAssessmentAvailable: 'ไม่มีการประเมิน',
    noAssessmentDataAvailable: 'ไม่มีข้อมูลการประเมินสำหรับเซสชันนี้'
  },

  zh: {
    // Header
    roleplayPractice: '角色扮演练习',
    serviceRecovery: '服务恢复',
    endSession: '结束会话',

    // Loading states
    preparingVoiceSession: '准备语音会话中...',
    connectingToCustomer: '连接客户中...',
    analyzingPerformance: '分析表现中...',
    generatingFeedback: '生成反馈中...',
    preparingChatSession: '准备聊天会话中...',
    settingUpConversation: '设置对话中...',

    // Practice controls
    startVoicePractice: '开始语音练习',
    startChatPractice: '开始聊天练习',
    voicePractice: '语音练习',
    chatPractice: '聊天练习',
    choosePracticeMode: '选择练习模式',
    realCallSimulation: '真实通话模拟',
    textBasedPractice: '线上文本对话',

    // Customer states
    customerIsSpeaking: '客户正在说话',
    customerIsListening: '客户正在听',

    // Section titles
    practiceDetails: '练习详情',
    practiceObjectives: '练习目标',
    mainIssue: '主要问题',
    serviceDelivery: '服务交付',
    customerDetails: '客户详情',
    policyInformation: '政策信息',

    // Service delivery subsections
    greetingIdentityVerification: '问候与身份验证',
    problemUnderstanding: '问题理解',
    empathyTone: '同理心与语调',
    resolutionRecovery: '解决方案与恢复',

    // Customer details fields
    orderId: '订单号',
    items: '物品',
    promisedVsActual: '承诺与实际',
    location: '位置',
    accountInformation: '账户信息',
    recentIncidents: '最近事件',
    signedUpOn: '注册时间',
    subscription: '订阅',
    loyaltyStatus: '会员状态',
    bookingReference: '预订参考号',

    // Service details fields (for Miso)
    serviceType: '服务类型',
    scheduledVsActual: '计划与实际',
    issue: '问题',

    // Policy fields
    trigger: '触发条件',
    remedy: '解决方案',
    policies: '政策',

    // Policy titles (for Cathay scenarios)
    typhoonDisruption: '台风中断',
    businessClassPriority: '商务舱优先',
    safetyRegulations: '安全法规',
    culturalSensitivity: '文化敏感性',
    shortHaulAmenities: '短途航线便利设施',
    businessClassService: '商务舱服务',
    customerNeedsDiscovery: '客户需求探索',
    passengerCelebration: '旅客庆祝服务',
    teamCollaboration: '团队协作',
    resourcefulness: '灵活应变能力',

    // Chat interface
    typeYourResponse: '输入您的回复',

    // Error states
    conversationTooBrief: '对话过于简短',
    conversationTooBriefMessage: '请进行更长的对话以获得准确的评估。',
    tryAgain: '再试一次',

    // Assessment page
    roleplayAssessment: '角色扮演评估',
    overallScore: '总体评分',
    excellent: '优秀',
    good: '良好',
    fair: '一般',
    poor: '较差',
    serviceDeliveryScore: '服务交付评分',
    policyApplication: '政策应用',
    practiceInfo: '练习信息',
    type: '类型',
    guest: '访客',
    completedAt: '完成时间',
    practiceAgain: '再次练习',
    jumpToSection: '跳转到部分',
    overview: '概述',
    summary: '总结',
    suggestedNextSteps: '建议的下一步',
    why: '原因',
    suggestion: '建议',
    strengths: '优势',
    toImprove: '改进领域',
    correction: '纠正',
    overallScoreLabel: '总体评分',

    // Assessment descriptions
    serviceDeliveryDesc: '服务交付描述',
    policyApplicationDesc: '政策应用描述',

    // Loading and error states
    loadingAssessmentData: '加载评估数据中...',
    failedToLoadAssessment: '无法加载评估',
    failedToLoadAssessmentData: '无法加载评估数据',
    returnToPractice: '返回练习',
    noAssessmentAvailable: '无可用评估',
    noAssessmentDataAvailable: '此会话无可用评估数据'
  },

  ms: {
    // Header
    roleplayPractice: 'Latihan lakonan peranan',
    serviceRecovery: 'Pemulihan perkhidmatan',
    endSession: 'Tamatkan sesi',

    // Loading states
    preparingVoiceSession: 'Menyediakan Sesi Suara',
    connectingToCustomer: 'Menyambung kepada {name} dan menetapkan audio...',
    analyzingPerformance: 'Menganalisis Prestasi Anda',
    generatingFeedback: 'Menjana maklum balas dan pandangan terperinci...',
    preparingChatSession: 'Menyediakan Sesi Sembang',
    settingUpConversation: 'Menetapkan perbualan anda dengan {name}...',

    // Practice controls
    startVoicePractice: 'Mulakan Latihan Suara',
    startChatPractice: 'Mulakan Latihan Sembang',
    voicePractice: 'Suara',
    chatPractice: 'Sembang',
    choosePracticeMode: 'Pilih mod',
    realCallSimulation: 'Latihan panggilan',
    textBasedPractice: 'Latihan teks',

    // Customer states
    customerIsSpeaking: 'Pelanggan sedang bercakap...',
    customerIsListening: 'Pelanggan sedang mendengar...',

    // Section titles
    practiceDetails: 'BUTIRAN LATIHAN',
    practiceObjectives: 'Objektif latihan',
    mainIssue: 'Isu utama',
    serviceDelivery: 'PENYAMPAIAN PERKHIDMATAN',
    customerDetails: 'BUTIRAN PELANGGAN',
    policyInformation: 'MAKLUMAT DASAR',

    // Service delivery subsections
    greetingIdentityVerification: 'Ucapan & Pengesahan Identiti',
    problemUnderstanding: 'Pemahaman Masalah',
    empathyTone: 'Empati & Nada',
    resolutionRecovery: 'Penyelesaian & Pemulihan',

    // Customer details fields
    orderId: 'ID Pesanan',
    items: 'Item',
    promisedVsActual: 'ETA Dijanjikan vs. Penghantaran Sebenar',
    location: 'Lokasi',
    accountInformation: 'Maklumat akaun',
    recentIncidents: 'Insiden dilaporkan (30 hari lepas)',
    signedUpOn: 'Mendaftar pada',
    subscription: 'Langganan',
    loyaltyStatus: 'Status Kesetiaan',
    bookingReference: 'Rujukan Tempahan',

    // Service details fields
    serviceType: 'Jenis Perkhidmatan',
    scheduledVsActual: 'Dijadualkan vs. Sebenar',
    issue: 'Isu',

    // Policy fields
    trigger: 'Pencetus',
    remedy: 'Penyelesaian',
    policies: 'dasar',

    // Policy titles (for Cathay scenarios)
    typhoonDisruption: 'Gangguan Taufan',
    businessClassPriority: 'Keutamaan Kelas Perniagaan',
    safetyRegulations: 'Peraturan Keselamatan',
    culturalSensitivity: 'Kepekaan Budaya',
    shortHaulAmenities: 'Kemudahan Penerbangan Jarak Dekat',
    businessClassService: 'Perkhidmatan Kelas Perniagaan',
    customerNeedsDiscovery: 'Penemuan Keperluan Pelanggan',
    passengerCelebration: 'Perayaan Penumpang',
    teamCollaboration: 'Kerjasama Pasukan',
    resourcefulness: 'Daya Cipta',

    // Chat interface
    typeYourResponse: 'Taip respons anda...',

    // Error states
    conversationTooBrief: 'Perbualan Terlalu Ringkas',
    conversationTooBriefMessage: 'Perbualan anda terlalu ringkas untuk menjana penilaian yang bermakna. Sila cuba lagi dengan perbualan yang lebih panjang yang merangkumi respons yang lebih terperinci.',
    tryAgain: 'Cuba Lagi',

    // Assessment page
    roleplayAssessment: 'Penilaian sesi lakonan peranan',
    overallScore: 'Skor keseluruhan',
    excellent: 'Cemerlang',
    good: 'Baik',
    fair: 'Sederhana',
    poor: 'Lemah',
    serviceDeliveryScore: 'Penyampaian Perkhidmatan',
    policyApplication: 'Aplikasi Dasar',
    practiceInfo: 'Maklumat latihan',
    type: 'Jenis',
    guest: 'Tetamu',
    completedAt: 'Selesai pada',
    practiceAgain: 'Latihan semula',
    jumpToSection: 'Lompat ke bahagian',
    overview: 'Tinjauan',
    summary: 'Ringkasan',
    suggestedNextSteps: 'Langkah seterusnya yang dicadangkan',
    why: 'Mengapa',
    suggestion: 'Cadangan',
    strengths: 'Kekuatan',
    toImprove: 'Untuk diperbaiki',
    correction: 'Pembetulan',
    overallScoreLabel: 'Skor keseluruhan',

    // Assessment descriptions
    serviceDeliveryDesc: 'Menilai keberkesanan interaksi dari sudut komunikasi dan pengalaman pelanggan.',
    policyApplicationDesc: 'Menilai ketepatan aplikasi dasar syarikat dan pematuhan terhadap piawaian dan protokol dalaman.',

    // Loading and error states
    loadingAssessmentData: 'Memuatkan data penilaian...',
    failedToLoadAssessment: 'Gagal memuatkan penilaian',
    failedToLoadAssessmentData: 'Gagal memuatkan data penilaian',
    returnToPractice: 'Kembali ke latihan',
    noAssessmentAvailable: 'Tiada penilaian tersedia',
    noAssessmentDataAvailable: 'Tiada data penilaian tersedia untuk sesi ini.'
  },

  'zh-Hant': {
    // Header
    roleplayPractice: '角色扮演練習',
    serviceRecovery: '服務恢復',
    endSession: '結束會話',

    // Loading states
    preparingVoiceSession: '準備語音會話中...',
    connectingToCustomer: '連接客戶中...',
    analyzingPerformance: '分析表現中...',
    generatingFeedback: '生成反饋中...',
    preparingChatSession: '準備聊天會話中...',
    settingUpConversation: '設置對話中...',

    // Practice controls
    startVoicePractice: '開始語音練習',
    startChatPractice: '開始聊天練習',
    voicePractice: '語音練習',
    chatPractice: '聊天練習',
    choosePracticeMode: '選擇練習模式',
    realCallSimulation: '真實通話模擬',
    textBasedPractice: '線上文本對話',

    // Customer states
    customerIsSpeaking: '客戶正在說話',
    customerIsListening: '客戶正在聽',

    // Section titles
    practiceDetails: '練習詳情',
    practiceObjectives: '練習目標',
    mainIssue: '主要問題',
    serviceDelivery: '服務交付',
    customerDetails: '客戶詳情',
    policyInformation: '政策資訊',

    // Service delivery subsections
    greetingIdentityVerification: '問候與身份驗證',
    problemUnderstanding: '問題理解',
    empathyTone: '同理心與語調',
    resolutionRecovery: '解決方案與恢復',

    // Customer details fields
    orderId: '訂單號',
    items: '項目',
    promisedVsActual: '承諾與實際',
    location: '位置',
    accountInformation: '帳戶資訊',
    recentIncidents: '最近事件',
    signedUpOn: '註冊時間',
    subscription: '訂閱',
    loyaltyStatus: '會員狀態',
    bookingReference: '預訂參考號',

    // Service details fields
    serviceType: '服務類型',
    scheduledVsActual: '計劃與實際',
    issue: '問題',

    // Policy fields
    trigger: '觸發條件',
    remedy: '解決方案',
    policies: '政策',

    // Policy titles
    typhoonDisruption: '颱風中斷',
    businessClassPriority: '商務艙優先',
    safetyRegulations: '安全法規',
    culturalSensitivity: '文化敏感性',
    shortHaulAmenities: '短途航線便利設施',
    businessClassService: '商務艙服務',
    customerNeedsDiscovery: '客戶需求探索',
    passengerCelebration: '旅客慶祝服務',
    teamCollaboration: '團隊協作',
    resourcefulness: '靈活應變能力',

    // Chat interface
    typeYourResponse: '輸入您的回覆',

    // Error states
    conversationTooBrief: '對話過於簡短',
    conversationTooBriefMessage: '請進行更長的對話以獲得準確的評估。',
    tryAgain: '再試一次',

    // Assessment page
    roleplayAssessment: '角色扮演評估',
    overallScore: '總體評分',
    excellent: '優秀',
    good: '良好',
    fair: '一般',
    poor: '較差',
    serviceDeliveryScore: '服務交付評分',
    policyApplication: '政策應用',
    practiceInfo: '練習資訊',
    type: '類型',
    guest: '訪客',
    completedAt: '完成時間',
    practiceAgain: '再次練習',
    jumpToSection: '跳轉到部分',
    overview: '概述',
    summary: '總結',
    suggestedNextSteps: '建議的下一步',
    why: '原因',
    suggestion: '建議',
    strengths: '優勢',
    toImprove: '改進領域',
    correction: '糾正',
    overallScoreLabel: '總體評分',

    // Assessment descriptions
    serviceDeliveryDesc: '評估從溝通和客戶體驗角度處理互動的效果。',
    policyApplicationDesc: '評估公司政策的準確應用及內部標準和規程的遵循程度。',

    // Loading and error states
    loadingAssessmentData: '載入評估資料中...',
    failedToLoadAssessment: '無法載入評估',
    failedToLoadAssessmentData: '無法載入評估資料',
    returnToPractice: '返回練習',
    noAssessmentAvailable: '無可用評估',
    noAssessmentDataAvailable: '此會話無可用評估資料'
  },
};

export function getTranslations(language: string): CXTranslations {
  return translations[language] || translations.en;
}